import 'core-js';

import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store, { registerComeinAiChatModule } from './store';

import i18n, { loadLoginLocale } from '@/plugins/i18n';
import { initDayjsLocaleSync } from '@/plugins/syncDayjsLocale';
import { initComeinUiPlusLocaleSync } from '@/plugins/syncComeinUiPlusLocale';

// 注册挂载全局组件/指令/插件
import Setup from './setup';
import { getStorageAuthInfo } from '@/utils/storageOperateApi';
import { installWorkBuddySessionRecovery } from '@/utils/workBuddySessionRecovery';
import { installWorkBuddySessionSync } from '@/utils/workBuddySessionSync';
import { installAuthSessionSync } from '@/utils/authSessionSync';

const app = createApp(App);
const AI_CHAT_WARMUP_TIMEOUT = 3000;

app.use(store);
app.use(i18n);
initDayjsLocaleSync(i18n);
initComeinUiPlusLocaleSync(app, i18n);
app.use(Setup);
// WorkBuddy 会话：登录态失效后凭 wb.login 静默重建，并感知其它窗口的退出登录
installWorkBuddySessionRecovery(router);
installWorkBuddySessionSync(router);
// 普通浏览器多标签页：别的标签页换账号 / 换机构重新登录后，提示本页刷新对齐新身份
installAuthSessionSync(router);

/**
 * 判断是否是 AI进宝入口路径
 *  1. 当前 URL 已在 AI进宝路径下
 *  2. 用户设置的默认菜单是 AI进宝
 * @returns {boolean} 是否是 AI进宝入口路径
 */
function isAiChatEntryPath() {
  if (location.pathname.startsWith('/aiChat')) return true;
  try {
    const userInfo = getStorageAuthInfo() || {};
    const menus = userInfo.menus || [];
    const defaultMenu = menus.find((m) => m.isDefaultRoute) || menus[0];
    const defaultPath = defaultMenu?.code || '';
    return defaultPath.startsWith('/aiChat');
  } catch {
    return false;
  }
}

/**
 * 在浏览器空闲时预热 AI进宝模块；Safari 不支持 requestIdleCallback 时退化为定时器。
 * @param {Function} callback - 需要延后执行的预热任务
 * @param {number} timeout - 最晚执行时间，避免预热任务长期不触发
 * @returns {number} 调度任务 ID
 */
function scheduleIdleWarmup(callback, timeout) {
  // 支持 requestIdleCallback 的浏览器优先在空闲期执行，降低首屏压力
  if (typeof window.requestIdleCallback === 'function') {
    return window.requestIdleCallback(callback, { timeout });
  }

  // Safari 等不支持 requestIdleCallback 的浏览器使用定时器兜底
  return window.setTimeout(callback, timeout);
}

if (isAiChatEntryPath()) {
  // 直接进 AI进宝，立即注册模块
  registerComeinAiChatModule();
} else {
  // 其他页面，空闲时预热，最晚 3s 内完成
  scheduleIdleWarmup(() => registerComeinAiChatModule(), AI_CHAT_WARMUP_TIMEOUT);
}
// 首屏只加载登录页所需语言模块，布局模块由路由守卫在进入认证区域时加载
loadLoginLocale(i18n.global.locale.value).then(() => {
  app.use(router);
  app.mount('#app');
});
